import { useEffect, useRef, useCallback, useState } from 'react';
import { usePOS } from '@/contexts/POSContext';
import { usePaymentDetection } from '@/hooks/usePaymentDetection';
import { useTokenPaymentDetection } from '@/hooks/useTokenPaymentDetection';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useWakeLock } from '@/hooks/useWakeLock';
import { useBCHPrice } from '@/hooks/useBCHPrice';
import { usePublishPaymentHeartbeat } from '@/hooks/useMerchantHeartbeat';
import { generateBCHUri, formatBCH, formatFiat, calcTax } from '@/lib/bch';
import { PUSD, generateTokenUri } from '@/lib/stablecoin';
import { toTokenAwareAddress } from '@/lib/fulcrum';
import { QRCodeCanvas } from '@/components/ui/qrcode';
import { CustomerDisplay } from './CustomerDisplay';
import { Transaction } from '@/lib/posStorage';
import { cn } from '@/lib/utils';

function playSuccessSound() {
  try {
    const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    
    const playNote = (freq: number, start: number, duration: number, gain: number) => {
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();
      osc.connect(gainNode);
      gainNode.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gainNode.gain.setValueAtTime(0, ctx.currentTime + start);
      gainNode.gain.linearRampToValueAtTime(gain, ctx.currentTime + start + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + duration);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + duration + 0.01);
    };

    // Happy ascending chime
    playNote(523.25, 0, 0.3, 0.3);    // C5
    playNote(659.25, 0.1, 0.3, 0.3);  // E5
    playNote(783.99, 0.2, 0.5, 0.3);  // G5
    playNote(1046.5, 0.35, 0.7, 0.25); // C6
  } catch {
    // Audio not available, silently fail
  }
}

// Auto-cancel timeout: 5 minutes online, warn at 60 seconds remaining
const TIMEOUT_SECONDS = 5 * 60;
const WARN_SECONDS = 60;

export function PaymentView() {
  const { settings, fiatAmount, discountAmount, discountLabel, tipAmount, note, pendingBCH, addTransaction, setView, cart, setFiatAmount, setPendingBCH, setTipAmount, setNote, setDiscount, clearCart, payWith, setPayWith } = usePOS();
  const hasDetectedRef = useRef(false);
  const isOnline = useOnlineStatus();
  const [showManualConfirm, setShowManualConfirm] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(TIMEOUT_SECONDS);
  const [customerMode, setCustomerMode] = useState(false);
  const publishPaymentHeartbeat = usePublishPaymentHeartbeat();

  // fiatAmount = original subtotal; discountAmount already calculated
  const discountedSubtotal = Math.max(0, fiatAmount - discountAmount);
  const { taxAmount, total: subtotalPlusTax } = calcTax(discountedSubtotal, settings.taxRate);
  const total = parseFloat((subtotalPlusTax + tipAmount).toFixed(2));

  // ── PUSD stablecoin support ────────────────────────────────────────────────
  // PUSD is pegged to USD. The merchant's total is in their chosen currency, so
  // we value it in USD via the BCH/USD price (pendingBCH is the total in BCH).
  const { data: usdPriceData } = useBCHPrice('USD');
  const pusdAmount = usdPriceData
    ? parseFloat((pendingBCH * usdPriceData.price).toFixed(2))
    : 0;

  // Only offer PUSD if enabled AND the merchant's address can be made token-aware.
  const tokenAddress = (() => {
    if (!settings.acceptPUSD || !settings.bchAddress) return null;
    try { return toTokenAwareAddress(settings.bchAddress); } catch { return null; }
  })();
  // Show the toggle whenever PUSD is enabled & the address is token-convertible.
  // The USD price may still be loading — that's handled separately below so a
  // transient price gap doesn't make the toggle vanish.
  const showAssetToggle = settings.acceptPUSD && !!tokenAddress;
  const canUsePUSD = showAssetToggle && pusdAmount > 0;
  const isPUSD = payWith === 'PUSD' && canUsePUSD;

  /**
   * Record a completed sale. `opts.asset` distinguishes BCH vs PUSD; for PUSD we
   * store the stablecoin display amount + symbol while still keeping bchAmount
   * (the dust-BCH value is negligible, so we record 0 for the token path).
   */
  const finalizeSale = useCallback(
    (opts: { txid: string; bchAmount: number; asset: 'BCH' | 'PUSD'; tokenAmount?: number }) => {
      if (hasDetectedRef.current) return;
      hasDetectedRef.current = true;

      playSuccessSound();
      publishPaymentHeartbeat(); // fire-and-forget: signal value was spent here

      const discSub = Math.max(0, fiatAmount - discountAmount);
      const { total: subPlusTax, taxAmount: ta } = calcTax(discSub, settings.taxRate);
      const grandTotal = parseFloat((subPlusTax + tipAmount).toFixed(2));

      const tx: Transaction = {
        id: crypto.randomUUID(),
        subtotal: fiatAmount,
        discountAmount,
        discountLabel,
        taxAmount: ta,
        taxLines: [],
        tipAmount,
        fiatAmount: grandTotal,
        bchAmount: opts.bchAmount,
        currency: settings.currency,
        asset: opts.asset,
        tokenAmount: opts.asset === 'PUSD' ? opts.tokenAmount : undefined,
        tokenSymbol: opts.asset === 'PUSD' ? PUSD.symbol : undefined,
        taxRate: settings.taxRate,
        taxName: settings.taxName,
        note: note.trim() || undefined,
        txid: opts.txid,
        address: opts.asset === 'PUSD' && tokenAddress ? tokenAddress : settings.bchAddress,
        timestamp: Date.now(),
        items: cart.length > 0
          ? cart.map((i) => ({ name: i.product.name, qty: i.qty, price: i.product.price }))
          : undefined,
      };
      addTransaction(tx);

      if (settings.skipReceipt) {
        // Fast reset — skip the success/receipt screen entirely
        setFiatAmount(0);
        setPendingBCH(0);
        setTipAmount(0);
        setDiscount(0, '');
        setNote('');
        clearCart();
        setView('checkout');
      } else {
        setView('success');
      }
    },
    [fiatAmount, discountAmount, discountLabel, tipAmount, note, settings, tokenAddress, addTransaction, setView, cart, setFiatAmount, setPendingBCH, setTipAmount, setNote, setDiscount, clearCart, publishPaymentHeartbeat]
  );

  // ── BCH detection (active unless paying with PUSD) ──
  const bch = usePaymentDetection(
    settings.bchAddress,
    pendingBCH,
    isOnline && !isPUSD, // pause when offline or when paying with PUSD
    ({ txid, amount }) => finalizeSale({ txid, bchAmount: amount, asset: 'BCH' })
  );

  // ── PUSD detection (active only when paying with PUSD) ──
  const pusd = useTokenPaymentDetection(
    tokenAddress ?? '',
    PUSD,
    pusdAmount,
    isOnline && isPUSD,
    ({ txid }) => finalizeSale({ txid, bchAmount: 0, asset: 'PUSD', tokenAmount: pusdAmount })
  );

  // Unified view of whichever detector is active.
  const status = isPUSD ? pusd.status : bch.status;
  const error = isPUSD ? pusd.error : bch.error;
  // IMPORTANT: depend on the individual reset fns (which are stable useCallbacks),
  // NOT the hook return objects (new identity each render) — otherwise this
  // `reset` changes every render and any effect cleaning up with it would tear
  // down the detection interval on every render (symptom: polls stuck at 1).
  const bchReset = bch.reset;
  const pusdReset = pusd.reset;
  const reset = useCallback(() => { bchReset(); pusdReset(); }, [bchReset, pusdReset]);
  const debug = bch.debug;
  const [showDebug, setShowDebug] = useState(false);

  // Keep the screen awake while waiting for payment
  useWakeLock(true);

  // Reset detection state on mount
  useEffect(() => {
    hasDetectedRef.current = false;
    return () => reset();
  }, [reset]);

  // Auto-cancel countdown — only when online (offline merchants may need it open indefinitely)
  useEffect(() => {
    if (!isOnline) return;
    setSecondsLeft(TIMEOUT_SECONDS);
    const tick = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(tick);
          reset();
          setView('checkout');
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [isOnline, reset, setView]);

  const bchUri = isPUSD && tokenAddress
    ? generateTokenUri(tokenAddress, PUSD, pusdAmount, { label: settings.merchantName })
    : generateBCHUri(settings.bchAddress, pendingBCH, settings.merchantName);
  const qrSize = Math.min(window.innerWidth - 80, 280);

  const handleCancel = () => {
    reset();
    setView('checkout');
  };

  /** Manual confirmation — used when offline; creates a transaction with a placeholder txid */
  const handleManualConfirm = useCallback(() => {
    finalizeSale({
      txid: `offline-${crypto.randomUUID()}`,
      bchAmount: isPUSD ? 0 : pendingBCH,
      asset: isPUSD ? 'PUSD' : 'BCH',
      tokenAmount: isPUSD ? pusdAmount : undefined,
    });
  }, [finalizeSale, isPUSD, pendingBCH, pusdAmount]);

  // Customer-facing full-screen overlay
  if (customerMode) {
    return (
      <CustomerDisplay
        bchUri={bchUri}
        fiatTotal={total}
        bchAmount={pendingBCH}
        currency={settings.currency}
        merchantName={settings.merchantName}
        merchantTagline={settings.merchantTagline}
        logoDataUrl={settings.logoDataUrl}
        brandColor={settings.brandColor ?? '#0AC18E'}
        assetLabel={isPUSD ? `${pusdAmount.toFixed(2)} PUSD` : undefined}
        assetIconUrl={isPUSD ? PUSD.iconUrl : undefined}
        scanHint={isPUSD ? 'Scan with a CashTokens wallet (e.g. Cashonize)' : undefined}
        onExit={() => setCustomerMode(false)}
      />
    );
  }

  return (
    <div className="flex flex-col h-full gap-4 items-center">
      {/* Asset toggle — shown when PUSD is enabled & the address is token-aware */}
      {showAssetToggle && (
        <div className="w-full shrink-0">
          <div className="flex bg-zinc-100 dark:bg-zinc-800 rounded-2xl p-1">
            <button
              onClick={() => setPayWith('BCH')}
              className={cn(
                'flex-1 py-2.5 rounded-xl text-sm font-bold transition-all',
                !isPUSD
                  ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-50 shadow-sm'
                  : 'text-zinc-400 dark:text-zinc-500'
              )}
            >
              ₿ Pay with BCH
            </button>
            <button
              onClick={() => canUsePUSD && setPayWith('PUSD')}
              disabled={!canUsePUSD}
              className={cn(
                'flex-1 py-2.5 rounded-xl text-sm font-bold transition-all',
                isPUSD
                  ? 'bg-white dark:bg-zinc-700 text-[#0AC18E] shadow-sm'
                  : 'text-zinc-400 dark:text-zinc-500',
                !canUsePUSD && 'opacity-50 cursor-not-allowed'
              )}
            >
              <span className="inline-flex items-center justify-center gap-1.5">
                {PUSD.iconUrl && (
                  <img src={PUSD.iconUrl} alt="" className="h-4 w-4 rounded-full" loading="lazy" />
                )}
                Pay with PUSD
              </span>
            </button>
          </div>
          {!canUsePUSD && (
            <p className="text-[11px] text-zinc-400 text-center mt-1.5">
              PUSD price loading… (needs BCH/USD rate)
            </p>
          )}
        </div>
      )}

      {/* If PUSD is enabled but the address can't be made token-aware, explain why. */}
      {settings.acceptPUSD && !tokenAddress && (
        <div className="w-full bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40 rounded-xl px-3 py-2 shrink-0">
          <p className="text-[11px] text-amber-700 dark:text-amber-400 leading-snug">
            PUSD is enabled but your BCH address couldn't be converted to a
            token-aware address. Check the address in Settings.
          </p>
        </div>
      )}

      {/* Header */}
      <div className="w-full text-center">
        <p className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-1">Payment Request</p>
        <div className="text-3xl font-bold text-zinc-900 dark:text-zinc-50">
          {formatFiat(total, settings.currency)}
        </div>
        <div className="flex items-center justify-center gap-1.5 text-lg font-mono font-medium text-[#0AC18E] mt-0.5">
          {isPUSD && PUSD.iconUrl && (
            <img src={PUSD.iconUrl} alt="PUSD" className="h-5 w-5 rounded-full" loading="lazy" />
          )}
          <span>{isPUSD ? `${pusdAmount.toFixed(2)} PUSD` : `${formatBCH(pendingBCH)} BCH`}</span>
        </div>
        {isPUSD && (
          <p className="text-xs text-zinc-400 mt-0.5">ParyonUSD stablecoin · $1-pegged</p>
        )}
        {/* Breakdown */}
        {(discountAmount > 0 || settings.taxRate > 0 || tipAmount > 0) && (
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-1.5 text-xs text-zinc-400">
            <span>Subtotal {formatFiat(fiatAmount, settings.currency)}</span>
            {discountAmount > 0 && <><span>·</span><span className="text-red-400 font-semibold">−{formatFiat(discountAmount, settings.currency)}</span></>}
            {settings.taxRate > 0 && <><span>·</span><span>{settings.taxName} {formatFiat(taxAmount, settings.currency)}</span></>}
            {tipAmount > 0 && <><span>·</span><span className="text-[#0AC18E] font-semibold">Tip {formatFiat(tipAmount, settings.currency)}</span></>}
          </div>
        )}
        {/* Note */}
        {note.trim() && (
          <div className="mt-2 bg-zinc-100 dark:bg-zinc-700 rounded-lg px-3 py-1.5 inline-block">
            <p className="text-xs text-zinc-600 dark:text-zinc-300 font-medium">📝 {note}</p>
          </div>
        )}
      </div>

      {/* QR Code */}
      <div className="bg-white rounded-3xl p-4 shadow-lg border border-zinc-100 flex items-center justify-center">
        <QRCodeCanvas
          value={bchUri}
          size={qrSize}
          level="M"
          className="rounded-xl"
        />
      </div>

      {/* Customer-facing mode button */}
      <button
        onClick={() => setCustomerMode(true)}
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-sm font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 active:scale-95 transition-all"
      >
        <span className="text-base">📺</span>
        Show to Customer
      </button>

      {/* Address — prominent trust section */}
      <div className="w-full bg-white dark:bg-zinc-800 rounded-2xl border-2 border-[#0AC18E]/30 overflow-hidden">
        {/* Trust header */}
        <div className="bg-[#0AC18E]/10 dark:bg-[#0AC18E]/15 px-4 py-2.5 flex items-center gap-2 border-b border-[#0AC18E]/20">
          <span className="text-[#0AC18E] text-base">✓</span>
          <p className="text-xs font-bold text-[#0AC18E] uppercase tracking-wider">
            Paying directly to merchant's wallet
          </p>
        </div>
        {/* Address */}
        <div className="px-4 py-3">
          <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-1.5">
            {isPUSD ? 'Token-Aware Address' : 'Destination Address'}
          </p>
          <p className="text-xs font-mono text-zinc-700 dark:text-zinc-200 break-all leading-relaxed">
            {isPUSD && tokenAddress ? tokenAddress : settings.bchAddress}
          </p>
          <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-2">
            Verify this matches the merchant's wallet before paying.
          </p>
        </div>
      </div>

      {/* Status indicator */}
      <div className="w-full flex flex-col items-center gap-2">
        {isOnline && status === 'waiting' && (
          <div className="flex items-center gap-3 py-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0AC18E] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[#0AC18E]"></span>
            </span>
            <span className="text-sm text-zinc-500 dark:text-zinc-400">
              {isPUSD ? 'Waiting for PUSD payment...' : 'Waiting for payment...'}
            </span>
          </div>
        )}
        {isOnline && secondsLeft <= WARN_SECONDS && secondsLeft > 0 && status === 'waiting' && (
          <div className="w-full bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/50 rounded-xl px-3 py-2 flex items-center gap-2">
            <span className="text-amber-500 text-sm shrink-0">⏱</span>
            <p className="text-xs text-amber-700 dark:text-amber-400 font-medium flex-1">
              Auto-cancelling in {secondsLeft}s
            </p>
            <button
              onClick={() => setSecondsLeft(TIMEOUT_SECONDS)}
              className="text-xs font-bold text-amber-600 dark:text-amber-400 hover:underline shrink-0"
            >
              Keep open
            </button>
          </div>
        )}
        {isOnline && status === 'error' && (
          <span className="text-sm text-red-400 py-2">{error ?? 'Detection error'}</span>
        )}

        {/* Diagnostics — tap the status to toggle. Helps verify detection is polling. */}
        {isOnline && (
          <button
            onClick={() => setShowDebug((v) => !v)}
            className="text-[10px] text-zinc-400 dark:text-zinc-600 underline"
          >
            {showDebug ? 'Hide' : 'Show'} detection diagnostics
          </button>
        )}
        {isOnline && showDebug && !isPUSD && (
          <div className="w-full bg-zinc-100 dark:bg-zinc-800 rounded-xl px-3 py-2 font-mono text-[11px] leading-relaxed text-zinc-600 dark:text-zinc-300 break-all">
            <div>asset: BCH</div>
            <div>polls: {debug.polls}</div>
            <div>watching: {debug.address || '—'}</div>
            <div>expecting ≥ {(debug.expectedBCH * 0.8).toFixed(8)} BCH</div>
            <div>backend: {debug.backend || '—'}</div>
            <div>last poll: UTXOs={debug.lastUtxoCount}, txs={debug.lastTxCount}</div>
            <div>
              last poll at:{' '}
              {debug.lastPolledAt ? new Date(debug.lastPolledAt).toLocaleTimeString() : '—'}
            </div>
            <div className={debug.lastError ? 'text-red-500' : ''}>
              error: {debug.lastError || 'none'}
            </div>
          </div>
        )}
        {isOnline && showDebug && isPUSD && (
          <div className="w-full bg-zinc-100 dark:bg-zinc-800 rounded-xl px-3 py-2 font-mono text-[11px] leading-relaxed text-zinc-600 dark:text-zinc-300 break-all">
            <div>asset: PUSD</div>
            <div>polls: {pusd.debug.polls}</div>
            <div>watching: {pusd.debug.address || '—'}</div>
            <div>expecting ≥ {pusd.debug.expectedUnits} base units</div>
            <div>server: {pusd.debug.server || '—'}</div>
            <div>token UTXOs seen: {pusd.debug.lastTokenUtxos}</div>
            <div>
              last poll at:{' '}
              {pusd.debug.lastPolledAt ? new Date(pusd.debug.lastPolledAt).toLocaleTimeString() : '—'}
            </div>
            <div className={pusd.debug.lastError ? 'text-red-500' : ''}>
              error: {pusd.debug.lastError || 'none'}
            </div>
          </div>
        )}

        {/* Offline notice */}
        {!isOnline && (
          <div className="w-full bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/50 rounded-xl px-4 py-3 flex items-start gap-3">
            <span className="text-amber-500 text-lg shrink-0 mt-0.5">📡</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-amber-700 dark:text-amber-400">You're offline</p>
              <p className="text-xs text-amber-600/80 dark:text-amber-500/80 mt-0.5 leading-relaxed">
                Auto-detection is paused. Use the manual confirm below once the customer pays.
              </p>
            </div>
          </div>
        )}

        {/* Manual confirm — always available as a fallback */}
        <div className="w-full">
          {!showManualConfirm ? (
            <button
              onClick={() => setShowManualConfirm(true)}
              className="w-full py-3.5 rounded-2xl text-sm font-semibold bg-zinc-50 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-700 active:scale-98 transition-all"
            >
              ✓ Customer has paid — confirm manually
            </button>
          ) : (
            <div className="w-full bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/50 rounded-2xl p-4 space-y-3">
              <div className="text-center">
                <p className="text-sm font-bold text-amber-700 dark:text-amber-400">Confirm payment received?</p>
                <p className="text-xs text-amber-600/70 dark:text-amber-500/70 mt-1 leading-relaxed">
                  Only confirm if you've verified the payment in your wallet. This records the sale immediately.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowManualConfirm(false)}
                  className="flex-1 py-3 rounded-xl text-sm font-semibold bg-white dark:bg-zinc-800 text-zinc-500 border border-amber-200 dark:border-amber-800/50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleManualConfirm}
                  className="flex-[2] py-3 rounded-xl text-sm font-bold bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30 hover:bg-[#09AF81] active:scale-98 transition-all"
                >
                  ✓ Yes, payment received
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cancel */}
      <button
        onClick={handleCancel}
        className="w-full py-4 rounded-2xl text-base font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700 active:scale-98 transition-all"
      >
        Cancel
      </button>
    </div>
  );
}
